import { Boxes } from 'lucide-react';
import {
  admin,
  palette,
  radii,
  shadow,
  transition,
  typography,
} from '../../../../styles/tokens';
import {
  COMPETITOR_PROFILES,
  type CompetitorImportProfile,
} from './competitorImportProfiles';

interface CompetitorGridProps {
  onPickCompetitor: (profile: CompetitorImportProfile) => void;
  onPickCatchAll: () => void;
}

// Frozen alphabetical order — defined in COMPETITOR_PROFILES. Do not sort.
export default function CompetitorGrid({
  onPickCompetitor,
  onPickCatchAll,
}: CompetitorGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 16,
      }}
    >
      {COMPETITOR_PROFILES.map(profile => {
        const Icon = profile.icon;
        return (
          <Card
            key={profile.id}
            onClick={() => onPickCompetitor(profile)}
            icon={<Icon size={28} color={palette.accent} strokeWidth={1.75} />}
            title={profile.displayName}
            hint={profile.hint}
          />
        );
      })}
      <Card
        onClick={onPickCatchAll}
        icon={<Boxes size={28} color={palette.g500} strokeWidth={1.75} />}
        title="My tool isn't here"
        hint="Upload a spreadsheet and we'll figure it out"
        dashed
      />
    </div>
  );
}

interface CardProps {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  hint: string;
  dashed?: boolean;
}

function Card({ onClick, icon, title, hint, dashed = false }: CardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 20,
        background: dashed ? palette.g50 : palette.white,
        border: dashed
          ? `2px dashed ${palette.g200}`
          : `1px solid ${palette.g150}`,
        borderRadius: radii.md,
        cursor: 'pointer',
        boxShadow: dashed ? shadow.none : shadow.xs,
        transition: `box-shadow ${transition.normal}, border-color ${transition.normal}`,
        fontFamily: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minHeight: 148,
      }}
      onMouseEnter={e => {
        if (!dashed) {
          e.currentTarget.style.boxShadow = shadow.cardHover;
          e.currentTarget.style.borderColor = admin.borderHover;
        }
      }}
      onMouseLeave={e => {
        if (!dashed) {
          e.currentTarget.style.boxShadow = shadow.xs;
          e.currentTarget.style.borderColor = palette.g150;
        }
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: radii.sm,
          background: dashed ? palette.g100 : palette.accentWash,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          style={{
            fontSize: typography.size.heading,
            fontWeight: typography.weight.semibold,
            color: admin.header,
            letterSpacing: '-0.005em',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: typography.size.caption,
            color: admin.caption,
            lineHeight: typography.lineHeight.normal,
          }}
        >
          {hint}
        </div>
      </div>
    </button>
  );
}
